import { useEffect, useRef } from "react";
import { useLayerStructuralActions } from "../components/MainVidPlayer/useLayerStructuralActions";
import { useMst } from "../stores/main";
import { isKeyboardShortcutSuppressedTarget } from "./keyboardShortcutGuards";

function isPrimaryShortcutModifier(e: KeyboardEvent): boolean {
  return e.metaKey || e.ctrlKey;
}

function getShortcutKey(e: KeyboardEvent): string {
  return e.key.toLowerCase();
}

export function isLayerCopyShortcut(e: KeyboardEvent): boolean {
  return (
    isPrimaryShortcutModifier(e) &&
    !e.altKey &&
    !e.shiftKey &&
    getShortcutKey(e) === "c"
  );
}

export function isLayerPasteShortcut(e: KeyboardEvent): boolean {
  return (
    isPrimaryShortcutModifier(e) &&
    !e.altKey &&
    !e.shiftKey &&
    getShortcutKey(e) === "v"
  );
}

export async function writeLayerClipboardText(text: string): Promise<boolean> {
  try {
    if (!navigator.clipboard?.writeText) {
      return false;
    }

    await navigator.clipboard.writeText(text);
    return true;
  } catch (error) {
    console.warn("[LayerClipboard] failed to write clipboard text", error);
    return false;
  }
}

async function readClipboardText(): Promise<string | undefined> {
  try {
    return await navigator.clipboard?.readText?.();
  } catch (error) {
    console.warn("[LayerClipboard] failed to read clipboard text", error);
    return undefined;
  }
}

type LayerClipboardShortcutHandlerOptions = {
  currentTab: string;
  layerInspectorEnabled: boolean;
  layerSelectionCount: number;
  copyLayers: () => Promise<string | undefined>;
  pasteLayers: (clipboardText?: string) => Promise<void>;
  readClipboardText: () => Promise<string | undefined>;
  writeClipboardText: (text: string) => Promise<boolean>;
  getFallbackClipboardText: () => string | undefined;
  setFallbackClipboardText: (text: string) => void;
};

export function handleLayerClipboardShortcut(
  e: KeyboardEvent,
  options: LayerClipboardShortcutHandlerOptions
): void {
  if (
    e.repeat ||
    isKeyboardShortcutSuppressedTarget(e.target) ||
    options.currentTab !== "layers"
  ) {
    return;
  }

  if (isLayerCopyShortcut(e)) {
    if (!options.layerInspectorEnabled || options.layerSelectionCount === 0) {
      return;
    }

    e.preventDefault();
    void (async () => {
      const clipboardText = await options.copyLayers();
      if (!clipboardText) {
        return;
      }

      options.setFallbackClipboardText(clipboardText);
      await options.writeClipboardText(clipboardText);
    })();
    return;
  }

  if (isLayerPasteShortcut(e)) {
    e.preventDefault();
    void (async () => {
      const systemClipboardText = await options.readClipboardText();
      const clipboardText =
        systemClipboardText === undefined
          ? options.getFallbackClipboardText()
          : systemClipboardText;
      await options.pasteLayers(clipboardText);
    })();
  }
}

/**
 * Hook that sets up keyboard shortcuts for layer clipboard actions:
 * - Cmd/Ctrl+C copies the current layer inspector selection
 * - Cmd/Ctrl+V pastes a Blings layer clipboard payload
 */
export function useLayerClipboardShortcuts() {
  const { currentTab, playerStore } = useMst();
  const { copyLayers, pasteLayers } = useLayerStructuralActions();
  const fallbackClipboardTextRef = useRef<string>();

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) =>
      handleLayerClipboardShortcut(e, {
        currentTab,
        layerInspectorEnabled:
          playerStore.layerInspectorEnabled &&
          playerStore.layerInspectorMode === "design",
        layerSelectionCount: playerStore.layerInspectorSelections.length,
        copyLayers,
        pasteLayers,
        readClipboardText,
        writeClipboardText: writeLayerClipboardText,
        getFallbackClipboardText: () => fallbackClipboardTextRef.current,
        setFallbackClipboardText: (text) => {
          fallbackClipboardTextRef.current = text;
        },
      });

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [copyLayers, currentTab, pasteLayers, playerStore]);
}
